import notificationService from '../services/notification.service.js'

import httpUtility from '../utils/http.utility.js'

import { useToast } from '../composables/useToast.js'

import { t } from '@/i18n'

const toast = useToast()

async function getNotifications(options) {
  try {
    return await notificationService.get.list(options)
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('common.errors.notificationsLoad')))
    return null
  }
}

async function getUnreadCount() {
  try {
    return await notificationService.get.unreadCount()
  } catch {
    return null
  }
}

async function markRead(id) {
  try {
    return await notificationService.update.read(id)
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('common.errors.notificationUpdate')))
    return null
  }
}

async function markAllRead() {
  try {
    await notificationService.update.readAll()
    return true
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('common.errors.notificationUpdate')))
    return false
  }
}

async function deleteNotification(id) {
  try {
    await notificationService.delete.notification(id)
    return true
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('common.errors.notificationDelete')))
    return false
  }
}

class NotificationController {
  get = {
    /**
     * Load notifications (optionally unread-only, paginated). Returns
     * `{ notifications, total, unreadCount }` on success, `null` on failure
     * (toasts the error).
     *
     * @param {{ unread?: boolean, page?: number, pageSize?: number }} [options]
     * @returns {Promise<{ notifications: object[], total: number, unreadCount: number }|null>}
     */
    async notifications(options) {
      return getNotifications(options)
    },

    /**
     * Read the unread badge count. Returns the count on success, `null` on
     * failure. Stays silent — this is a frequent background poll, so a
     * transient failure must not spam toasts.
     *
     * @returns {Promise<number|null>}
     */
    async unreadCount() {
      return getUnreadCount()
    },
  }

  update = {
    /**
     * Mark one notification read. Returns the updated notification on
     * success, `null` on failure (toasts the error).
     *
     * @param {string} id
     * @returns {Promise<object|null>}
     */
    async read(id) {
      return markRead(id)
    },

    /**
     * Mark every notification read. Returns `true` on success, `false` on
     * failure (toasts the error).
     *
     * @returns {Promise<boolean>}
     */
    async readAll() {
      return markAllRead()
    },
  }

  delete = {
    /**
     * Delete one notification. Returns `true` on success, `false` on failure
     * (toasts the error).
     *
     * @param {string} id
     * @returns {Promise<boolean>}
     */
    async notification(id) {
      return deleteNotification(id)
    },
  }
}

export default new NotificationController()
